import React, { useCallback, useEffect, useState } from 'react';
import { Play, Settings2, Lock, RefreshCw, Film } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { isPortraitVideo } from '@/lib/videoOrientation';
import { videoGenState } from '@/lib/videoGenState';
import { videoManageLabel, fmtClock } from '@/lib/videoManageState';
import { VideoTrimEditor } from '@/components/seller/live/VideoTrimEditor';

// LIVE-PRODUCT-VIDEO-SELLER-UI-1 · 상품별 라이브 소개 영상 (비공개 · 판매자 본인만).
export const VIDEO_STATUS = {
  PENDING: '처리 대기', PROCESSING: '영상 생성 중', READY: '재생 가능', FAILED: '처리 실패',
  STALE: '구간 변경 · 재생성 필요', HELD: '처리 보류',
};
const fmtDate = (s) => (s ? new Date(s).toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' }) : '-');
const fmtDur = (sec) => (sec ? `${Math.floor(sec / 60)}:${String(Math.round(sec % 60)).padStart(2, '0')}` : '-');
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};
const fmtSec = (s) => (s ? new Date(s).toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'medium' }) : '-');
const ERR_KO = {
  video_not_found: '영상을 찾을 수 없거나 권한이 없습니다.', video_not_ready: '아직 재생할 수 없는 영상입니다.',
  storage_not_configured: '영상 저장소가 설정되지 않았습니다.', recording_not_completed: '방송 녹화가 완료된 뒤 생성할 수 있습니다.',
  segment_cancelled: '취소된 소개 구간은 대표영상으로 설정할 수 없습니다.',
  video_hidden: '소비자 노출을 중지한 영상은 대표영상으로 설정할 수 없습니다.',
  outside_recording: '원본 녹화 범위를 벗어난 시각입니다.', invalid_range: '종료 시각은 시작 시각보다 늦어야 합니다.',
};
const fmtTime = (s) => (s ? new Date(s).toLocaleTimeString('ko-KR', { hour12: false }) : '-');
const TONE = { green: 'bg-green-500/10 text-green-700', red: 'bg-red-500/10 text-red-600', amber: 'bg-amber-500/10 text-amber-700', gray: 'bg-black/5 dark:bg-white/10' };
const errMsg = (e, fb) => { const d = e?.response?.data?.detail; return (typeof d === 'string' && (ERR_KO[d] || d)) || fb; };
const toIso = (s) => { const d = new Date(s); return s && !Number.isNaN(d.getTime()) ? d.toISOString() : null; };

export { isPortraitVideo };

export const VideoCard = ({ v, onPlay, onManage, onPrimary, primaryBusy }) => {
  const lbl = videoManageLabel(v);
  const pl = v.playable;
  return (
    <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 p-3 rounded-lg border border-black/10 dark:border-white/10" data-testid={`live-video-card-${v.segment_id}`}>
      <div className="w-16 h-12 rounded bg-black/80 flex items-center justify-center shrink-0"><Film size={18} className="text-white/70" /></div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold line-clamp-2 break-all" data-testid="live-video-title">
          {v.intro_order ? <span className="text-gray-500 font-medium mr-1" data-testid="live-video-intro-order">{v.intro_order === 1 ? '첫 번째 소개' : `${v.intro_order}번째 소개`} ·</span> : null}
          {v.schedule_title || v.label || '방송'}
        </p>
        <p className="text-xs text-gray-500" data-testid="live-video-meta">{fmtDate(v.started_at)} · {fmtDur(pl?.duration_sec ?? v.video?.duration_sec)}{pl?.version ? ` · v${pl.version}` : ''}</p>
        <p className="text-[11px] text-gray-400" data-testid="live-video-source-range">원본 구간 {fmtTime(v.original?.started_at)}~{fmtTime(v.original?.ended_at)}{v.edit_count ? ` · 수정 ${fmtTime(v.started_at)}~${fmtTime(v.ended_at)}` : ''}</p>
        <div className="flex flex-wrap gap-1 mt-1">
          <span className={`text-[11px] px-1.5 py-0.5 rounded ${TONE[lbl.tone]}`} data-testid="live-video-status">{lbl.text}</span>
          {v.hidden && <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-700 text-white" data-testid={`live-video-hidden-badge-${v.segment_id}`}>소비자 숨김</span>}
          <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-500/10 text-gray-600 inline-flex items-center gap-0.5" data-testid="live-video-private"><Lock size={10} />비공개</span>
          {pl && <span className="text-[11px] px-1.5 py-0.5 rounded bg-black/5 dark:bg-white/10" data-testid="live-video-source">{pl.source === 'clean' ? '클린 영상' : '방송화면 영상'}</span>}
          {pl?.audio === 'silent' && <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700">무음</span>}
          {v.is_primary && <span className="text-[11px] px-1.5 py-0.5 rounded bg-[#FF3B30] text-white font-bold" data-testid={`live-video-primary-badge-${v.segment_id}`}>{onPrimary?.mode === 'manual' ? '대표영상' : '기본 대표영상'}</span>}
        </div>
      </div>
      {onPrimary && pl && !v.hidden && !v.is_primary && (
        <button type="button" disabled={primaryBusy} onClick={() => onPrimary.set(v.segment_id)}
                className="order-last sm:order-none w-full sm:w-auto px-3 py-1.5 rounded-full border border-[#FF3B30]/40 text-[#FF3B30] text-xs font-bold disabled:opacity-40"
                data-testid={`live-video-set-primary-${v.segment_id}`}>대표영상으로 설정</button>
      )}
      <button type="button" disabled={!pl} onClick={() => onPlay(v)} className="p-2 rounded-full bg-[#FF3B30] text-white disabled:opacity-30" aria-label="재생" data-testid={`live-video-play-${v.segment_id}`}><Play size={14} /></button>
      <button type="button" onClick={() => onManage(v)} className="p-2 rounded-full border border-black/10 dark:border-white/20" aria-label="관리" data-testid={`live-video-manage-${v.segment_id}`}><Settings2 size={14} /></button>
    </div>
  );
};

export const VideoPlayDialog = ({ v, onClose }) => {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(null);
  const [portrait, setPortrait] = useState(false);
  useEffect(() => {
    if (!v) { return; }
    setUrl(null); setErr(null); setPortrait(false);
    api.get(`/seller/product-videos/${v.segment_id}/play-url`, { params: v.playVersion ? { version: v.playVersion } : {} })
      .then(({ data }) => setUrl(data.url))
      .catch((e) => setErr(errMsg(e, '영상 주소를 불러오지 못했습니다.')));
  }, [v]);
  return (
    <Dialog open={!!v} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className={portrait ? 'max-w-sm' : 'max-w-2xl'} data-testid="live-video-play-dialog">
        <DialogHeader><DialogTitle>{v?.schedule_title || '라이브 소개 영상'}{v?.playVersion ? ` · v${v.playVersion}` : ''}</DialogTitle></DialogHeader>
        {err && <p className="text-sm text-red-600" data-testid="live-video-play-error">{err}</p>}
        {url && !err && (
          <video src={url} controls playsInline className="block mx-auto max-w-full max-h-[70vh] rounded bg-black object-contain"
                 data-testid="live-video-player" data-orientation={portrait ? 'portrait' : 'landscape'}
                 onLoadedMetadata={(e) => setPortrait(isPortraitVideo(e.currentTarget.videoWidth, e.currentTarget.videoHeight))}
                 onError={() => setErr('영상을 재생할 수 없습니다. 잠시 후 다시 열어 주세요.')} />
        )}
        {!url && !err && <p className="text-sm text-gray-500">불러오는 중…</p>}
        <p className="text-xs text-gray-500">비공개 영상 · 판매자 본인만 재생할 수 있습니다.</p>
      </DialogContent>
    </Dialog>
  );
};

const useSellerProducts = (open) => {
  const [list, setList] = useState([]);
  useEffect(() => {
    if (!open) { return; }
    api.get('/seller/products', { params: { page: 1, limit: 200 } })
      .then(({ data }) => setList(Array.isArray(data) ? data : (data.items || data.products || [])))
      .catch(() => setList([]));
  }, [open]);
  return list;
};

const VersionList = ({ v, onPlay }) => (
  <div className="space-y-1" data-testid="live-video-versions">
    <p className="text-xs font-bold">보관 버전 <span className="font-normal text-gray-500">(같은 소개 구간의 수정 버전 · 모두 보존)</span></p>
    {(v.versions || []).map((x) => (
      <div key={x.version} className="flex items-center gap-2 text-xs" data-testid={`live-video-version-${x.version}`}>
        <span className="font-bold">v{x.version}</span>
        <span className="text-gray-500 flex-1">{fmtClock(x.duration_sec)} · {fmtDate(x.ready_at)}</span>
        {v.playable?.version === x.version && <span className="px-1.5 rounded bg-green-500/10 text-green-700">사용 중</span>}
        <button type="button" onClick={() => onPlay({ ...v, playVersion: x.version })} className="p-1.5 rounded-full border" aria-label="버전 재생" data-testid={`live-video-version-play-${x.version}`}><Play size={11} /></button>
      </div>
    ))}
  </div>
);

const HideToggle = ({ v, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try {
      await api.patch(`/seller/broadcasts/${v.schedule_id}/product-segments/${v.segment_id}`, { hidden: !v.hidden });
      toast.success(v.hidden ? '소비자에게 다시 표시합니다.' : '소비자 노출을 중지했습니다 · 영상 파일은 보관됩니다.');
      onSaved();
    } catch (e) { toast.error(errMsg(e, '변경 실패')); } finally { setBusy(false); }
  };
  return (
    <button type="button" disabled={busy} onClick={toggle} className="w-full py-2 rounded border border-black/15 dark:border-white/20 text-xs font-bold disabled:opacity-40" data-testid="live-video-hide-toggle">
      {v.hidden ? '소비자에게 다시 표시' : '소비자 노출 중지 (파일·이력 보관)'}
    </button>
  );
};

export const SegmentManageDialog = ({ v, onClose, onSaved, onPlay }) => {
  const products = useSellerProducts(!!v);
  const [form, setForm] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!v) { return; }
    setForm({ product_id: v.product_id || '', started_at: toLocalInput(v.started_at), ended_at: toLocalInput(v.ended_at), cancelled: false });
    setConfirming(false);
  }, [v]);
  if (!v || !form) return null;
  const changes = {};
  if ((form.product_id || null) !== (v.product_id || null)) changes.product_id = form.product_id || null;
  if (form.started_at !== toLocalInput(v.started_at)) changes.started_at = toIso(form.started_at);
  if (form.ended_at !== toLocalInput(v.ended_at)) changes.ended_at = toIso(form.ended_at);
  const sIso = toIso(form.started_at); const eIso = toIso(form.ended_at);
  const timeErr = !sIso || !eIso ? '시작·종료 시각을 입력해 주세요.' : sIso >= eIso ? '종료 시각은 시작 시각보다 늦어야 합니다.' : null;
  if (form.cancelled) changes.cancelled = true;
  const pname = (id) => products.find((p) => p.product_id === id)?.name || id || '미연결';
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/seller/broadcasts/${v.schedule_id}/product-segments/${v.segment_id}`, changes);
      toast.success(changes.started_at || changes.ended_at ? '보정 저장 · 영상 재생성이 필요합니다.' : '저장되었습니다.');
      onSaved();
    } catch (e) { toast.error(errMsg(e, '저장 실패')); } finally { setBusy(false); }
  };
  const gen = videoGenState(v.video, form.cancelled);
  const regen = async () => {
    if (busy || !gen.can) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/seller/broadcasts/${v.schedule_id}/product-videos/split`, { segment_ids: [v.segment_id] });
      toast.success(data.queued ? '영상 생성을 시작했습니다 · 완료되면 목록이 자동으로 갱신됩니다.' : data.already_processing ? '이미 생성 중입니다.' : '새로 만들 영상이 없습니다.');
      onSaved();
    } catch (e) { toast.error(errMsg(e, '요청 실패')); } finally { setBusy(false); }
  };
  const field = 'w-full px-2 py-1.5 rounded border border-black/15 dark:border-white/20 bg-transparent text-sm';
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="live-video-manage-dialog">
        <DialogHeader><DialogTitle>소개 영상 관리</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-xs text-gray-500" data-testid="live-video-manage-status">상태: {videoManageLabel(v).text} · 현재 v{v.playable?.version || '-'} · 보정 {v.edit_count}회{v.hidden ? ' · 소비자 숨김' : ''}</p>
          {v.video?.last_error && <p className="text-xs text-red-600" data-testid="live-video-last-error">{v.video.last_error}</p>}
          {v.trim_ready
            ? <VideoTrimEditor v={v} onSaved={onSaved} disabled={busy || v.video?.status === 'PROCESSING'} />
            : v.playable && <p className="text-[11px] text-gray-500" data-testid="live-video-trim-unavailable">이 영상은 앞뒤 구간 편집을 지원하지 않습니다 · 아래 시작·종료 시각으로 보정할 수 있습니다.</p>}
          {v.versions?.length > 0 && <VersionList v={v} onPlay={onPlay} />}
          {v.playable && <HideToggle v={v} onSaved={onSaved} />}
          <p className="text-xs font-bold pt-1">상품 연결 · 시각 보정</p>
          <label className="block">연결 상품
            <select className={field} value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} data-testid="live-video-product-select">
              <option value="">연결 안 함 (미등록 · 보류)</option>
              {v.product_id && !products.some((p) => p.product_id === v.product_id) && <option value={v.product_id}>{v.product_id}</option>}
              {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
            </select>
          </label>
          <label className="block">시작 시각<input type="datetime-local" step="1" className={field} value={form.started_at} onChange={(e) => setForm({ ...form, started_at: e.target.value })} data-testid="live-video-start-input" /></label>
          <label className="block">종료 시각<input type="datetime-local" step="1" className={field} value={form.ended_at} onChange={(e) => setForm({ ...form, ended_at: e.target.value })} data-testid="live-video-end-input" /></label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.cancelled} onChange={(e) => setForm({ ...form, cancelled: e.target.checked })} data-testid="live-video-cancel-check" />잘못 선택한 구간 (취소 · 이력은 보존)</label>
          {timeErr && <p className="text-xs text-red-600" data-testid="live-video-time-error">{timeErr}</p>}
          {confirming && (
            <div className="p-2 rounded bg-black/5 dark:bg-white/10 text-xs space-y-0.5" data-testid="live-video-change-preview">
              {'product_id' in changes && <p>상품: {pname(v.product_id)} → {pname(changes.product_id)} (영상 재사용)</p>}
              {changes.started_at && <p>시작: {fmtSec(v.started_at)} → {fmtSec(changes.started_at)}</p>}
              {changes.ended_at && <p>종료: {fmtSec(v.ended_at)} → {fmtSec(changes.ended_at)}</p>}
              {(changes.started_at || changes.ended_at) && <p className="text-amber-700">시간 변경 · 저장 후 새 버전 생성 필요 (기존 버전 유지)</p>}
              {changes.cancelled && <p className="text-red-600">구간 취소 · 목록에서 숨김</p>}
            </div>
          )}
          <div className="flex gap-2 pt-1">
            {!confirming
              ? <button type="button" disabled={!Object.keys(changes).length || !!timeErr} onClick={() => setConfirming(true)} className="flex-1 py-2 rounded bg-[#FF3B30] text-white font-bold disabled:opacity-30" data-testid="live-video-review-btn">변경 확인</button>
              : <button type="button" disabled={busy || !!timeErr} onClick={save} className="flex-1 py-2 rounded bg-[#FF3B30] text-white font-bold" data-testid="live-video-save-btn">저장</button>}
            <button type="button" disabled={busy || !gen.can} onClick={regen} className="px-3 py-2 rounded border border-black/15 dark:border-white/20 inline-flex items-center gap-1 disabled:opacity-30" data-testid="live-video-regen-btn"><RefreshCw size={13} className={busy ? 'animate-spin' : ''} />{busy ? '요청 중…' : v.playable && gen.can ? '수정본 생성' : '영상 생성'}</button>
          </div>
          <p className={`text-[11px] ${gen.can ? 'text-gray-500' : 'text-amber-700'}`} data-testid="live-video-regen-reason">{gen.reason}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const VideoListPanel = ({ title, testId, fetchUrl, emptyText, primaryUrl }) => {
  const [items, setItems] = useState(null);
  const [mode, setMode] = useState('auto');
  const [pBusy, setPBusy] = useState(false);
  const [play, setPlay] = useState(null);
  const [manage, setManage] = useState(null);
  const load = useCallback(() => {
    api.get(fetchUrl).then(({ data }) => { setItems(data.videos || []); setMode(data.primary_mode || 'auto'); }).catch(() => setItems([]));
  }, [fetchUrl]);
  useEffect(() => { load(); }, [load]);
  const processing = (items || []).some((v) => v.video?.status === 'PROCESSING');
  useEffect(() => {
    if (!processing) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [processing, load]);
  const setPrimary = async (segmentId) => {
    if (pBusy) return;
    setPBusy(true);
    try {
      await api.put(primaryUrl, { segment_id: segmentId });
      toast.success(segmentId ? '대표영상을 변경했습니다.' : '첫 소개영상(기본)으로 되돌렸습니다.');
      load();
    } catch (e) { toast.error(errMsg(e, '대표영상 변경 실패 · 기존 대표영상이 유지됩니다.')); } finally { setPBusy(false); }
  };
  const ready = (items || []).filter((v) => v.video?.status === 'READY').length;
  const primary = primaryUrl ? { set: setPrimary, mode } : null;
  return (
    <div className="space-y-2" data-testid={testId}>
      <p className="text-sm font-bold">{title} {items && <span className="text-gray-500 font-normal">({items.length})</span>}</p>
      {primaryUrl && items?.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-500" data-testid="live-video-primary-note">
          <span>첫 번째 소개영상이 기본으로 표시됩니다. 같은 상품을 다시 소개하면 영상이 추가되며, 원하는 영상을 대표영상으로 변경할 수 있습니다.</span>
          {mode === 'manual' && ready > 0 && (
            <button type="button" disabled={pBusy} onClick={() => setPrimary(null)} className="px-2 py-1 rounded-full border text-[11px] font-bold text-gray-700 dark:text-gray-200 disabled:opacity-40"
                    data-testid="live-video-primary-reset">기본 선택으로 되돌리기</button>
          )}
        </div>
      )}
      {items === null && <p className="text-xs text-gray-500">불러오는 중…</p>}
      {items && !items.length && <p className="text-xs text-gray-500" data-testid={`${testId}-empty`}>{emptyText}</p>}
      {(items || []).map((v) => <VideoCard key={v.segment_id} v={v} onPlay={setPlay} onManage={setManage} onPrimary={primary} primaryBusy={pBusy} />)}
      <VideoPlayDialog v={play} onClose={() => setPlay(null)} />
      <SegmentManageDialog v={manage} onClose={() => setManage(null)} onSaved={() => { setManage(null); load(); }} onPlay={setPlay} />
    </div>
  );
};

export const ProductLiveVideos = ({ productId }) => (
  <div className="mt-5 pt-4 border-t border-black/10 dark:border-white/10">
    <VideoListPanel title="라이브 소개 영상" testId="product-live-videos" fetchUrl={`/seller/products/${productId}/videos`}
                    primaryUrl={`/seller/products/${productId}/primary-video`}
                    emptyText="아직 이 상품의 라이브 소개 영상이 없습니다." />
  </div>
);

export const UnlinkedLiveVideos = () => (
  <div className="p-4 rounded-lg border border-black/10 dark:border-white/10">
    <VideoListPanel title="상품 미연결 소개 영상" testId="unlinked-live-videos" fetchUrl="/seller/product-videos/unlinked"
                    emptyText="상품에 연결되지 않은 소개 영상이 없습니다." />
  </div>
);
